<template>
<div class="m-top-10 panel panel-success" id="allocationPanel">
    <div v-if="showHeader" class="panel-heading centered">
        <h4 style="margin: 0 auto; text-align: center;">Allocations</h4>
    </div>
    <div class="panel-body centered timecard-head">
        <div class="timecard">
            <div class="col-xs-3">Show/Episode</div>
            <div class="col-xs-2">%</div>
            <div class="col-xs-2">Hours</div>
            <div class="col-xs-2">Wages</div>
            <div class="col-xs-2">Allowances</div>
            <div class="col-xs-1">&nbsp;</div>
        </div>

        <div v-for="(row, index) in currentAllocation" :key="row.key" class="col-sm-12 timecard">
            <!-- Project: readonly label for default rows, selectable for user-added rows -->
            <div class="col-xs-3">
                <input v-if="!row.added" class="form-control" type="text" readonly tabindex="-1" :value="row.name"/>
                <select v-else class="form-control" v-model="row.id" @change="onProjectPicked(index)">
                    <option value="">— pick a project —</option>
                    <option v-for="p in optionsForRow(row)" :key="p.id" :value="p.id">{{ p.code ? p.code + ' — ' + p.name : p.name }}</option>
                </select>
            </div>
            <div class="col-xs-2"><input class="form-control" :class="{ 'allocation-edited': row.edited }" type="text" v-model="row.percent" @change="updateFields(index,'percent')"/></div>
            <div class="col-xs-2"><input class="form-control" type="text" :value="row.hours" readonly tabindex="-1"/></div>
            <div class="col-xs-2"><input class="form-control" type="text" v-model="row.amount" @change="updateFields(index,'amount')"/></div>
            <div class="col-xs-2"><input class="form-control" type="text" v-model="row.allowances" @change="updateFields(index,'allowances')"/></div>
            <div class="col-xs-1">
                <button type="button" class="btn btn-link" title="Remove row" @click="removeRow(index)">&times;</button>
            </div>
        </div>

        <!-- Unallocated remainder. Should be zero when the split is valid. -->
        <div class="col-sm-12 timecard">
            <div class="col-xs-3"><input class="form-control" type="text" readonly tabindex="-1" value="UNALLOCATED"/></div>
            <div class="col-xs-2"><input class="form-control" :class="{ 'form-error': !isZero(unallocatedPercent) }" type="text" :value="unallocatedPercent" readonly tabindex="-1"/></div>
            <div class="col-xs-2"><input class="form-control" :class="{ 'form-error': !isZero(unallocatedHours) }" type="text" :value="unallocatedHours" readonly tabindex="-1"/></div>
            <div class="col-xs-2"><input class="form-control" :class="{ 'form-error': !isZero(unallocatedAmount) }" type="text" :value="unallocatedAmount" readonly tabindex="-1"/></div>
            <div class="col-xs-2"><input class="form-control" :class="{ 'form-error': !isZero(unallocatedAllowances) }" type="text" :value="unallocatedAllowances" readonly tabindex="-1"/></div>
            <div class="col-xs-1">&nbsp;</div>
        </div>

        <div class="col-sm-12 centered m-top-10">
            <button type="button" class="btn btn-default btn-sm" @click="addRow">+ Add project</button>
            <button type="button" class="btn btn-default btn-sm" @click="resetToDefault" title="Rebuild the default day-based split">Reset split</button>
            <span v-if="!isValid" class="text-danger m-left-10">Split must total 100% and match total pay &amp; allowances.</span>
        </div>
    </div>
</div>
</template>
<script>
import { defineComponent } from 'vue'
import TimecardMixin from '../mixins/TimecardMixin';
import UtilitiesMixin from '../mixins/UtilitiesMixin.js';
import COAMixin from '../mixins/COAMixin.js';

// Sentinel id for the "no project" (null project_id) allocation line.
const NO_PROJECT = '__none__';

export default defineComponent({
    name: "Allocation",

    mixins: [ TimecardMixin, UtilitiesMixin, COAMixin ],

    emits: ['changed'],

    props: {
        // Previously-saved allocation rows (from overrides.allocation). Empty => build the default split.
        allocation: { type: Array, default: () => [] },
        // All projects available to the entity.
        projects:   { type: Array, default: () => [] },
        // Day -> { project_id } input maps (week 1 / week 2).
        week:       { type: Object, default: () => ({}) },
        week2:      { type: Object, default: () => ({}) },
        // Computed pay breakdown documents (brokenHours / brokenHours2).
        broken:     { type: Object, default: () => ({}) },
        broken2:    { type: Object, default: () => ({}) },
        // Entity flag — the allocation UI is only meaningful when projects are in use.
        showProjects: { default: false },
        // Show the "Allocations" panel heading (hidden on the bulk page, where the grid
        // row already identifies the timecard).
        showHeader: { default: true },
    },

    data() {
        return {
            currentAllocation: [],
            rowSeq: 0,
            suppressEmit: false,
        }
    },

    computed: {
        // Is this a bi-weekly timecard? (broken2 carries a real second week.)
        isBiweekly() {
            return !!(this.broken2 && this.broken2.week && this.broken2.week.total);
        },

        // --- Grand totals the split must reconcile to ---
        totalPay() {
            return this.weekPay(this.broken) + (this.isBiweekly ? this.weekPay(this.broken2) : 0);
        },
        totalAllowances() {
            return this.weekAllow(this.broken) + (this.isBiweekly ? this.weekAllow(this.broken2) : 0);
        },
        totalHours() {
            return this.weekHours(this.broken) + (this.isBiweekly ? this.weekHours(this.broken2) : 0);
        },
        // Wages = total pay minus allowances (allowances are a weekly, not per-day, bucket).
        wagesTotal() {
            return this.round2(this.totalPay - this.totalAllowances);
        },

        // Count of paid days across both weeks — the denominator for the default allowance split.
        daysWithPay() {
            return this.countPaidDays(this.broken) + (this.isBiweekly ? this.countPaidDays(this.broken2) : 0);
        },

        // Projects that actually appear on a paid day (plus a null-project line when any paid day has none).
        activeKeys() {
            const keys = [];
            const seen = {};
            const scan = (week, broken) => {
                for (const dayKey in (week || {})) {
                    if (!/^day_/.test(dayKey)) continue;
                    const day = week[dayKey];
                    if (!this.dayHasPay(broken, dayKey)) continue;
                    const pid = day && day.project_id ? String(day.project_id) : NO_PROJECT;
                    if (!seen[pid]) { seen[pid] = true; keys.push(pid); }
                }
            };
            scan(this.week, this.broken);
            if (this.isBiweekly) scan(this.week2, this.broken2);
            return keys;
        },

        unallocatedPercent() {
            return this.round4(100 - this.sumField('percent'));
        },
        unallocatedAmount() {
            return this.round2(this.wagesTotal - this.sumField('amount'));
        },
        unallocatedAllowances() {
            return this.round2(this.totalAllowances - this.sumField('allowances'));
        },
        unallocatedHours() {
            return this.round2(this.totalHours - this.sumField('hours'));
        },

        isValid() {
            return this.isZero(this.unallocatedPercent) &&
                   this.isZero(this.unallocatedAmount) &&
                   this.isZero(this.unallocatedAllowances);
        },
    },

    methods: {
        // ---------- tiny helpers ----------
        num(v) { const n = parseFloat(v); return isNaN(n) ? 0 : n; },
        round2(v) { return Math.round((this.num(v) + Number.EPSILON) * 100) / 100; },
        round4(v) { return Math.round((this.num(v) + Number.EPSILON) * 10000) / 10000; },
        isZero(v) { return Math.abs(this.num(v)) < 0.01; },

        weekPay(broken)   { return this.num((((broken || {}).week || {}).total || {}).pay); },
        weekHours(broken) { return this.num((((broken || {}).week || {}).total || {}).hours); },
        weekAllow(broken) { return this.num((((broken || {}).week || {}).allowances || {}).pay); },

        dayHasPay(broken, dayKey) {
            return this.num((((broken || {})[dayKey] || {}).total || {}).pay) > 0;
        },
        countPaidDays(broken) {
            let c = 0;
            for (const k in (broken || {})) {
                if (/^day_/.test(k) && this.dayHasPay(broken, k)) c++;
            }
            return c;
        },

        sumField(field) {
            return this.currentAllocation.reduce((t, r) => t + this.num(r[field]), 0);
        },

        // ---------- default split (actual pay summed per project) ----------
        // Sum the real per-day wages + hours for the days assigned to `key` (NO_PROJECT => null project_id).
        subtotalsByWeek(result, week, broken, key) {
            if (!week || !broken) return result;
            const dayKeys = Object.entries(week)
                .filter((e) => {
                    if (!/^day_/.test(e[0]) || !e[1]) return false;
                    const pid = e[1].project_id ? String(e[1].project_id) : NO_PROJECT;
                    return pid === key;
                })
                .map((e) => e[0]);

            for (const dayKey of dayKeys) {
                const dayData = broken[dayKey];
                if (!dayData || !dayData.total) continue;
                const penalties = this.num((dayData.penalties || {}).pay);
                result.wages += this.num(dayData.total.pay) + penalties;
                result.hours += this.num((dayData.total || {}).hours);
                result.dayCount++;
            }
            return result;
        },

        sumsByKey(key) {
            let result = { wages: 0, hours: 0, allowances: 0, dayCount: 0 };
            result = this.subtotalsByWeek(result, this.week, this.broken, key);
            if (this.isBiweekly) result = this.subtotalsByWeek(result, this.week2, this.broken2, key);
            // Allowances split by share of paid days (weekly bucket, no per-day attribution).
            const denom = this.daysWithPay || 1;
            result.allowances = this.round2(this.totalAllowances * (result.dayCount / denom));
            result.wages = this.round2(result.wages);
            result.hours = this.round2(result.hours);
            return result;
        },

        projectName(key) {
            if (key === NO_PROJECT) return '(No project)';
            const p = (this.projects || []).find((x) => String(x.id) === String(key));
            if (!p) return 'Project ' + key;
            return p.code ? p.code + ' — ' + p.name : p.name;
        },
        // The project CODE string (used by ProPay as customer_series); null for the no-project line.
        projectCode(key) {
            if (key === NO_PROJECT || !key) return null;
            const p = (this.projects || []).find((x) => String(x.id) === String(key));
            return p ? (p.code || null) : null;
        },

        makeRow(key, sums, added) {
            return {
                key: 'r' + (this.rowSeq++),
                id: key,
                code: this.projectCode(key),
                name: this.projectName(key),
                amount: sums ? sums.wages : 0,
                allowances: sums ? sums.allowances : 0,
                hours: sums ? sums.hours : 0,
                percent: 0,
                edited: false,
                added: !!added,
            };
        },

        buildDefault() {
            const rows = [];
            for (const key of this.activeKeys) {
                rows.push(this.makeRow(key, this.sumsByKey(key), false));
            }
            this.currentAllocation = rows;
            this.recomputeDerived();
            this.fixRounding();
        },

        resetToDefault() {
            this.buildDefault();
        },

        // ---------- editing + redistribution ----------
        updateFields(index, type) {
            const row = this.currentAllocation[index];
            if (!row) return;
            row.edited = true;

            if (type === 'percent') {
                // Combined share of total pay -> split into wages/allowances by the global ratio.
                const combined = this.num(row.percent) / 100 * this.totalPay;
                const wRatio = this.totalPay ? this.wagesTotal / this.totalPay : 1;
                row.amount = this.round2(combined * wRatio);
                row.allowances = this.round2(combined * (1 - wRatio));
            } else {
                // amount or allowances edited directly.
                row.amount = this.round2(row.amount);
                row.allowances = this.round2(row.allowances);
            }
            this.redistribute();
        },

        // Spread the remaining wages/allowances proportionally across un-edited rows.
        // If every row is edited (nothing left to absorb), the leftover simply shows in UNALLOCATED.
        redistribute() {
            const locked = this.currentAllocation.filter((r) => r.edited);
            const open = this.currentAllocation.filter((r) => !r.edited);

            const remWages = this.round2(this.wagesTotal - locked.reduce((t, r) => t + this.num(r.amount), 0));
            const remAllow = this.round2(this.totalAllowances - locked.reduce((t, r) => t + this.num(r.allowances), 0));

            if (open.length) {
                this.spread(open, 'amount', remWages);
                this.spread(open, 'allowances', remAllow);
            }
            this.recomputeDerived();
            this.fixRounding();
        },

        // Distribute `total` across rows weighted by their current value in `field` (equal if all zero).
        spread(rows, field, total) {
            const weights = rows.map((r) => this.num(r[field]));
            const weightSum = weights.reduce((a, b) => a + b, 0);
            const equal = weightSum <= 0;
            rows.forEach((r, i) => {
                r[field] = this.round2(equal ? total / rows.length : total * (weights[i] / weightSum));
            });
        },

        // Recompute percent (from wages+allowances) and hours (from percent) for every row. Hours are read-only.
        recomputeDerived() {
            for (const r of this.currentAllocation) {
                const combined = this.num(r.amount) + this.num(r.allowances);
                r.percent = this.totalPay ? this.round4(combined / this.totalPay * 100) : 0;
                r.hours = this.round2(this.num(r.percent) / 100 * this.totalHours);
            }
        },

        // Push any penny/rounding residual onto the largest row so column sums tie out exactly.
        fixRounding() {
            if (!this.currentAllocation.length) return;
            const biggest = this.largestRow();
            if (!biggest) return;
            const resWages = this.round2(this.wagesTotal - this.sumField('amount'));
            const resAllow = this.round2(this.totalAllowances - this.sumField('allowances'));
            if (Math.abs(resWages) < 0.5) biggest.amount = this.round2(this.num(biggest.amount) + resWages);
            if (Math.abs(resAllow) < 0.5) biggest.allowances = this.round2(this.num(biggest.allowances) + resAllow);
            // Re-derive percent/hours after the residual nudge.
            for (const r of this.currentAllocation) {
                const combined = this.num(r.amount) + this.num(r.allowances);
                r.percent = this.totalPay ? this.round4(combined / this.totalPay * 100) : 0;
                r.hours = this.round2(this.num(r.percent) / 100 * this.totalHours);
            }
        },

        largestRow() {
            let best = null;
            for (const r of this.currentAllocation) {
                if (!best || (this.num(r.amount) + this.num(r.allowances)) > (this.num(best.amount) + this.num(best.allowances))) best = r;
            }
            return best;
        },

        // ---------- add / remove rows ----------
        // Project choices for a row's dropdown: active projects not already taken by ANOTHER row.
        // The row's own current selection is kept, so it stays displayed after it's picked.
        optionsForRow(row) {
            const usedByOthers = this.currentAllocation
                .filter((r) => r !== row)
                .map((r) => String(r.id));
            return (this.projects || []).filter((p) =>
                p && p.id && parseInt(p.status) !== 0 && !usedByOthers.includes(String(p.id)));
        },
        addRow() {
            this.currentAllocation.push(this.makeRow('', null, true));
        },
        onProjectPicked(index) {
            const row = this.currentAllocation[index];
            if (!row || !row.id) return;
            row.name = this.projectName(row.id);
            row.code = this.projectCode(row.id);
            // A freshly-picked project starts at zero and absorbs its share via redistribution.
            row.edited = false;
            this.redistribute();
        },
        removeRow(index) {
            this.currentAllocation.splice(index, 1);
            this.redistribute();
        },

        // ---------- (re)build decision ----------
        maybeBuild() {
            // Prefer a previously-saved split; otherwise build the default from the day/project map.
            if (Array.isArray(this.allocation) && this.allocation.length) {
                this.currentAllocation = this.allocation.map((r) => Object.assign(
                    { key: 'r' + (this.rowSeq++), edited: false, added: false, hours: 0 }, r
                ));
                this.recomputeDerived();
            } else {
                this.buildDefault();
            }
        },

        emitChanged() {
            if (this.suppressEmit) return;
            // Emit a clean, storable payload (drop view-only keys). Skip an added row whose
            // project hasn't been picked yet (id is empty), and drop empty rows that carry no
            // pay — a 0% / $0 line (e.g. "(No project)" when every day is on a project) isn't
            // worth persisting and shouldn't ride into the ProPay export.
            const payload = this.currentAllocation
                .filter((r) => r.id === NO_PROJECT || (r.id !== '' && r.id != null))
                .map((r) => ({
                    id: r.id, code: r.code || null, name: r.name, percent: this.num(r.percent), hours: this.num(r.hours),
                    amount: this.num(r.amount), allowances: this.num(r.allowances),
                }))
                .filter((r) => !(this.isZero(r.percent) && this.isZero(r.amount) && this.isZero(r.allowances)));
            this.$emit('changed', payload, this.isValid);
        },
    },

    watch: {
        // Rebuild the default split when the computed pay changes — but only if the user hasn't touched it.
        broken: {
            deep: true,
            handler() {
                const touched = this.currentAllocation.some((r) => r.edited);
                const saved = Array.isArray(this.allocation) && this.allocation.length;
                if (!touched && !saved) this.buildDefault();
            },
        },
        currentAllocation: {
            deep: true,
            handler() { this.emitChanged(); },
        },
    },

    mounted() {
        this.maybeBuild();
    },
});
</script>
<style scoped>
/* "Manually pinned" indicator for an edited row — intentionally NOT .form-error, which is
   reserved for real validation errors (the UNALLOCATED cells) and blocks the timecard from saving. */
.allocation-edited {
    background-color: #fff3cd;
    border-color: #ffe08a;
}
</style>
